'use client';

import type { ReactNode } from 'react';
import React from 'react';
import { Box } from '@terra/ui-styled-system';
import { Splitter } from '@terra/ui-design-system';
import { BottomPanel } from './BottomPanel';
import { MainContentTabPanel } from './MainContentTabPanel';
import { useBottomPanel, useMainContentTabs } from '../../store/layoutStore';
import { DEFAULT_BOTTOM_PANEL_SPLITTER_SIZES } from '../../store/bottomPanelStore';
import { useAppTargetId } from '../../store/projectStore';
import { useSourceCodeInfo } from '../../hooks/dashboard/useSourceCodeInfo';
import { ErrorBoundary } from '../ErrorBoundary';

interface MainContentAreaProps {
  children?: ReactNode;
}

const MIN_BOTTOM_PANEL_SIZE = 4;
const MAX_BOTTOM_PANEL_SIZE = 85;
const COLLAPSE_THRESHOLD_SIZE = 4;
const COLLAPSED_BOTTOM_PANEL_SIZE = MIN_BOTTOM_PANEL_SIZE;

export function MainContentArea({ children: _children }: MainContentAreaProps) {
  const [selectedEndpoint, setSelectedEndpoint] = React.useState<
    | {
        method: string;
        path: string;
        domain: string;
      }
    | undefined
  >();

  const {
    activeTab,
    closeTab,
    activePageEntry,
    tabs: mainContentTabs,
  } = useMainContentTabs();

  const {
    tabs: bottomTabs,
    selectTab: selectBottomTab,
    toggleCollapse: toggleBottomCollapse,
    isCollapsed: isBottomCollapsed,
    splitterSizes: persistedSplitterSizes,
    setSplitterSizes: persistSplitterSizes,
    currentEndpointId,
    open: openBottomPanel,
    close: closeBottomPanel,
    selectedTab: selectedBottomTab,
  } = useBottomPanel();
  const appTargetId = useAppTargetId();
  const { data: sourceCodeInfo } = useSourceCodeInfo(appTargetId ?? '');
  const hideCodeGraphTab = sourceCodeInfo?.isConnected === false;
  const visibleBottomTabs = React.useMemo(
    () =>
      hideCodeGraphTab
        ? bottomTabs.filter((tab) => tab.id !== 'code-graph')
        : bottomTabs,
    [bottomTabs, hideCodeGraphTab],
  );
  const visibleActiveBottomTab =
    visibleBottomTabs.find((tab) => tab.isActive) ?? visibleBottomTabs[0];

  React.useEffect(() => {
    if (hideCodeGraphTab && selectedBottomTab === 'code-graph') {
      selectBottomTab('terminal');
    }
  }, [hideCodeGraphTab, selectBottomTab, selectedBottomTab]);

  React.useEffect(() => {
    if (!hideCodeGraphTab) {
      return;
    }

    for (const tab of mainContentTabs) {
      if (tab.type === 'code-list') {
        closeTab(tab.id);
      }
    }
  }, [hideCodeGraphTab, mainContentTabs, closeTab]);

  const defaultSizes = React.useMemo<[number, number]>(() => {
    const [persistedMainSize, persistedBottomSize] =
      persistedSplitterSizes ?? DEFAULT_BOTTOM_PANEL_SPLITTER_SIZES;
    const clampedBottomSize = Math.min(
      Math.max(persistedBottomSize, MIN_BOTTOM_PANEL_SIZE),
      MAX_BOTTOM_PANEL_SIZE,
    );
    const normalizedMainSize = 100 - clampedBottomSize;

    if (clampedBottomSize === persistedBottomSize) {
      return [persistedMainSize, persistedBottomSize];
    }

    return [normalizedMainSize, clampedBottomSize];
  }, [persistedSplitterSizes]);

  const effectiveSizes = React.useMemo<[number, number]>(
    () =>
      isBottomCollapsed
        ? [100 - COLLAPSED_BOTTOM_PANEL_SIZE, COLLAPSED_BOTTOM_PANEL_SIZE]
        : defaultSizes,
    [defaultSizes, isBottomCollapsed],
  );

  const handleResizeEnd = React.useCallback(
    ({ size }: { size: number[] }) => {
      const bottomSize = size[1];

      if (bottomSize === undefined) {
        return;
      }

      if (bottomSize <= COLLAPSE_THRESHOLD_SIZE) {
        closeBottomPanel();

        return;
      }

      if (isBottomCollapsed) {
        openBottomPanel();
      }

      const clampedBottomSize = Math.min(bottomSize, MAX_BOTTOM_PANEL_SIZE);
      const normalizedMainSize = 100 - clampedBottomSize;

      persistSplitterSizes?.([normalizedMainSize, clampedBottomSize]);
    },
    [
      closeBottomPanel,
      isBottomCollapsed,
      openBottomPanel,
      persistSplitterSizes,
    ],
  );

  const [controlledSplitterSizes, setControlledSplitterSizes] =
    React.useState<[number, number]>(effectiveSizes);

  React.useEffect(() => {
    setControlledSplitterSizes(effectiveSizes);
  }, [effectiveSizes]);

  const handleResize = React.useCallback(({ size }: { size: number[] }) => {
    const mainSize = size[0];
    const bottomSize = size[1];

    if (mainSize === undefined || bottomSize === undefined) {
      return;
    }

    setControlledSplitterSizes([mainSize, bottomSize]);
  }, []);

  React.useEffect(() => {
    if (activeTab?.type === 'endpoint' && activeTab.data) {
      const data = activeTab.data as Record<string, unknown>;

      if (
        typeof data.method === 'string' &&
        typeof data.fullPath === 'string' &&
        typeof data.domain === 'string'
      ) {
        setSelectedEndpoint({
          method: data.method,
          path: data.fullPath,
          domain: data.domain,
        });
      }
    }
  }, [activeTab]);

  const mainContent = (
    <Box h="full" display="flex" flexDirection="column" w="full">
      <Box flex="1" overflow="hidden" w="full">
        {activeTab ? (
          <ErrorBoundary
            key={activeTab.id}
            scopeId={activeTab.id}
            onClose={() => closeTab(activeTab.id)}
          >
            <MainContentTabPanel
              tab={activeTab}
              activePageEntry={activePageEntry}
            />
          </ErrorBoundary>
        ) : null}
      </Box>
    </Box>
  );

  const bottomPanel = (
    <BottomPanel
      tabs={visibleBottomTabs}
      activeTab={visibleActiveBottomTab}
      onTabSelect={(tabId: string) => {
        selectBottomTab(tabId as 'terminal' | 'code-graph' | 'decoder');
        if (isBottomCollapsed) {
          openBottomPanel();
        }
      }}
      onToggleCollapse={toggleBottomCollapse}
      isCollapsed={isBottomCollapsed}
      selectedEndpoint={selectedEndpoint}
      endpointId={currentEndpointId}
    />
  );

  return (
    <Box h="full" bg="bg.canvas" w="full">
      <Splitter.Root
        orientation="vertical"
        h="100%"
        panels={[
          { id: 'main-content' },
          {
            id: 'bottom-panel',
            minSize: isBottomCollapsed
              ? COLLAPSED_BOTTOM_PANEL_SIZE
              : MIN_BOTTOM_PANEL_SIZE,
            maxSize: isBottomCollapsed
              ? COLLAPSED_BOTTOM_PANEL_SIZE
              : MAX_BOTTOM_PANEL_SIZE,
          },
        ]}
        defaultSize={effectiveSizes}
        size={controlledSplitterSizes}
        onResize={isBottomCollapsed ? undefined : handleResize}
        onResizeEnd={isBottomCollapsed ? undefined : handleResizeEnd}
        borderWidth="0"
        gap={0}
      >
        <Splitter.Panel
          id="main-content"
          borderWidth="0"
          borderRadius="none"
          alignItems="stretch"
          justifyContent="flex-start"
        >
          {mainContent}
        </Splitter.Panel>

        <Splitter.ResizeTrigger
          id="main-content:bottom-panel"
          h="px"
          minH="px"
          margin="0"
          bg="border.subtle"
          position="relative"
          cursor={isBottomCollapsed ? 'default' : 'row-resize'}
          zIndex={10}
          display={isBottomCollapsed ? 'none' : undefined}
          _after={{
            content: '""',
            position: 'absolute',
            inset: '-4px 0 -4px 0',
            height: '12px',
            bg: 'transparent',
            cursor: 'row-resize',
            zIndex: 10,
          }}
        />

        <Splitter.Panel
          id="bottom-panel"
          borderWidth="0"
          borderRadius="none"
          alignItems="stretch"
          justifyContent={isBottomCollapsed ? 'flex-end' : 'flex-start'}
          overflow="hidden"
        >
          {bottomPanel}
        </Splitter.Panel>
      </Splitter.Root>
    </Box>
  );
}
